import React, { useState, useContext, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faHome, 
  faSearch, 
  faCog, 
  faPersonPraying, 
  faHandHoldingHeart, 
  faChartLine, 
  faWallet,
  faEllipsisVertical, // Kebab menu icon
  faBullseye, // Icon for accountability feature
  faUserFriends, // Alternative icon for accountability
  faUsers, // Icon for user management
  faUserMd, // Icon for applications
  faBell, // Icon for notifications
  faEnvelope, // Icon for messages
  faHeart, // Icon for support
  faChurch, // Icon for find church
  faDownload, // Icon for download mobile app
  faFileContract, // Icon for Terms of Service
  faShieldHalved // Icon for Child Safety Standards
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import SettingsModal from './settings_modal';
import SupportClowzaModal from './SupportClowzaModal';
import { MyContext } from "../MainContext";
import { backendUrl } from '../utils/variables';

const Footer = ({page}) => {
  const {user, adminData} = useContext(MyContext);
  const [showSettings, setShowSettings] = useState(false);
  const [showSupportModal, setShowSupportModal] = useState(false);
  const [prayerCount, setPrayerCount] = useState(0);
  const [therapyCount, setTherapyCount] = useState(0);
  const [messageCount, setMessageCount] = useState(0);
  const [showKebabMenu, setShowKebabMenu] = useState(false);
  const navigate = useNavigate();

  // Check if user has admin access and permissions
  const isAdmin = adminData && adminData.role;
  //const canViewAnalytics = adminData?.permissions?.analytics || false;
  //const canSendNotifications = adminData?.permissions?.notif || false;
  const canManageUsers = adminData?.permissions?.users || false;
  const canManageApplications = adminData?.permissions?.applications || false;

  // Debug logging
  //console.log('Footer - adminData:', adminData);
  //console.log('Footer - isAdmin:', isAdmin);
  //console.log('Footer - canSendNotifications:', canSendNotifications);
  //console.log('Footer - canViewAnalytics:', canViewAnalytics);

  // Function to fetch prayer requests count
  const fetchPrayerRequestsCount = async () => {
    if (!user || !user.user_id) return;

    //console.log('Footer - user:', user.user_id);
    
    try {
      const response = await fetch(`${backendUrl}/my_prayer_responses.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: user.user_id
        })
      });
      
      const data = await response.json();
      if (data.success) {
        setPrayerCount(data.count);
      }
    } catch (error) {
      console.error('Error fetching prayer requests count:', error);
    }
  };

  // Function to fetch therapy sessions count
  const fetchTherapySessionsCount = async () => {
    if (!user || !user.user_id) return;
    
    try {
      const response = await fetch(`${backendUrl}/my_therapy_sessions.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: user.user_id
        })
      });
      
      const data = await response.json();
      if (data.success) {
        let count = data.is_therapist 
          ? data.sessions.filter(session => session.status === 'pending').length
          : data.sessions.filter(session => session.status === 'active').length;
        setTherapyCount(count);
      }
    } catch (error) {
      console.error('Error fetching therapy sessions count:', error);
    }
  };

  // Function to fetch unread messages count
  const fetchUnreadMessagesCount = async () => {
    if (!user || !user.user_id) return;
    
    try {
      const response = await fetch(`${backendUrl}/get_unread_messages_count.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: user.user_id
        })
      });
      
      const data = await response.json();
      if (data.success) {
        setMessageCount(data.count || 0);
      }
    } catch (error) {
      console.error('Error fetching unread messages count:', error);
    }
  };

  useEffect(() => {
    fetchPrayerRequestsCount();
    fetchTherapySessionsCount();
    fetchUnreadMessagesCount();
    
    const interval = setInterval(() => {
      fetchPrayerRequestsCount();
      fetchTherapySessionsCount();
      fetchUnreadMessagesCount();
    }, 60000);
    
    return () => clearInterval(interval);
  }, [user]);

  const toggleSettings = () => {
    setShowSettings(!showSettings);
    setShowKebabMenu(false);
  };

  const closeSettings = () => {
    setShowSettings(false);
  };

  const toggleKebabMenu = () => {
    setShowKebabMenu(!showKebabMenu);
  };

  const handleNavigation = (route) => {
    navigate(route);
    setShowKebabMenu(false);
  };

  const handleSupportClick = () => {
    setShowSupportModal(true);
    setShowKebabMenu(false);
  };

  return (
    <>
      <nav className="navbar navbar-light bg-light fixed-bottom d-block">
        <div className="nav justify-content-around w-100" id="footer-nav">
          {/* Home */}
          <a className={`nav-link ${page === "home" ? 'nav-link-selected' : ''}`} onClick={() => navigate(`/`)}>
            <FontAwesomeIcon className="menu-icon" icon={faHome} />
          </a>

          {/* Prayer */}
          <a className={`nav-link ${page === "prayer" ? 'nav-link-selected' : ''}`} onClick={() => navigate(`/prayers`)}>
            <div className="position-relative">
              <FontAwesomeIcon className="menu-icon" icon={faPersonPraying} />
              {prayerCount > 0 && (
                <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger pr-badge">
                  {prayerCount > 99 ? '99+' : prayerCount}
                </span>
              )}
            </div>
          </a>

          {/* Therapy */}
          <a className={`nav-link ${page === "therapy" ? 'nav-link-selected' : ''}`} onClick={() => navigate(`/therapy`)}>
            <div className="position-relative">
              <FontAwesomeIcon className="menu-icon" icon={faHandHoldingHeart} />
              {therapyCount > 0 && (
                <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger pr-badge">
                  {therapyCount > 99 ? '99+' : therapyCount}
                </span>
              )}
            </div>
          </a>

          {/* Accountability */}
          <a className={`nav-link ${page === "goals" ? 'nav-link-selected' : ''}`} onClick={() => navigate(`/goals`)}>
            <FontAwesomeIcon className="menu-icon" icon={faBullseye} />
          </a>

          {/* Messages */}
          <a className={`nav-link ${page === "messages" ? 'nav-link-selected' : ''}`} onClick={() => navigate(`/messages`)}>
            <div className="position-relative">
              <FontAwesomeIcon className="menu-icon" icon={faEnvelope} />
              {messageCount > 0 && (
                <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger pr-badge">
                  {messageCount > 99 ? '99+' : messageCount}
                </span>
              )}
            </div>
          </a>

          {/* Kebab Menu */}
          <div className="nav-item dropdown">
            <a 
              className={`nav-link ${showKebabMenu ? 'nav-link-selected' : ''}`} 
              onClick={toggleKebabMenu}
            >
              <FontAwesomeIcon className="menu-icon" icon={faEllipsisVertical} />
            </a>
            {showKebabMenu && (
              <div className="dropdown-menu show position-absolute bottom-100 mb-2" style={{ right: '5px' }}>


                {/* Search */}
                <button 
                  className="dropdown-item" 
                  onClick={() => handleNavigation('/search')}
                >
                  <FontAwesomeIcon icon={faSearch} className="me-2" />
                  Search
                </button>

                {/* Groups */}
                <button 
                  className="dropdown-item" 
                  onClick={() => handleNavigation('/groups')}
                >
                  <FontAwesomeIcon icon={faUsers} className="me-2" style={{ color: '#48887b' }} />
                  Groups
                </button>

                {/* Find a church near me */}
                <button 
                  className="dropdown-item" 
                  onClick={() => handleNavigation('/find-church')}
                >
                  <FontAwesomeIcon icon={faChurch} className="me-2" />
                  Find a church near me
                </button>

                {/* Wallet */}
                <button 
                  className="dropdown-item" 
                  onClick={() => handleNavigation('/wallet')}
                >
                  <FontAwesomeIcon icon={faWallet} className="me-2" />
                  Wallet
                </button>

                {/* Admin Dashboard */}
                {isAdmin && (
                  <>
                    <button 
                      className="dropdown-item" 
                      onClick={() => handleNavigation('/dashboard')}
                    >
                      <FontAwesomeIcon icon={faChartLine} className="me-2" />
                      Admin Dashboard
                    </button>
                      

                    {/* User Management - only if user has users permission */}
                    {canManageUsers && 3 > 4 && (
                      <button 
                        className="dropdown-item" 
                        onClick={() => handleNavigation('/manage-users')}
                      >
                        <FontAwesomeIcon icon={faUsers} className="me-2" />
                        Manage Users
                      </button>
                    )}

                    {/* Applications - only if user has applications permission */}
                    {canManageApplications && 3 > 4 && (
                      <button 
                        className="dropdown-item" 
                        onClick={() => handleNavigation('/manage-applications')}
                      >
                        <FontAwesomeIcon icon={faUserMd} className="me-2" />
                        Applications
                      </button>
                    )}

          
                  </>
                )}

                {/* Support Clowza */}
                <button 
                  className="dropdown-item" 
                  onClick={handleSupportClick}
                >
                  <FontAwesomeIcon icon={faHeart} className="me-2" style={{ color: '#48887b' }} />
                  Support Clowza
                </button>

                {/* Terms of Service */}
                <button 
                  className="dropdown-item" 
                  onClick={() => handleNavigation('/terms-of-service')}
                >
                  <FontAwesomeIcon icon={faFileContract} className="me-2" />
                  Terms of Service
                </button>

                {/* Child Safety Standards */}
                <button 
                  className="dropdown-item" 
                  onClick={() => {
                    setShowKebabMenu(false);
                    window.location.href = '/child-safety-standards.html';
                  }}
                >
                  <FontAwesomeIcon icon={faShieldHalved} className="me-2" />
                  Child Safety Standards
                </button>

                {/* Settings */}
                <button 
                  className="dropdown-item" 
                  onClick={toggleSettings}
                >
                  <FontAwesomeIcon icon={faCog} className="me-2" />
                  Settings
                </button>
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* Overlay to close menu when clicking outside */}
      {showKebabMenu && (
        <div 
          className="position-fixed" 
          style={{ top: 0, left: 0, right: 0, bottom: 0, zIndex: 999 }}
          onClick={() => setShowKebabMenu(false)}
        />
      )}

      {showSettings && <SettingsModal close={closeSettings} />}
      <SupportClowzaModal
        show={showSupportModal}
        onHide={() => setShowSupportModal(false)}
      />
    </>
  );
};

export default Footer;